import { useState, useEffect } from 'react'
import { kycApprovalAPI } from '../services/kycApprovalService'
import { getEmptyFormData, validateAllSections, ALL_SECTIONS } from '../utils/kycApprovalUtils'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import { toast } from 'react-toastify'

/**
 * Custom hook for managing KYC approval form state
 * Handles data loading, updates, and save/approve operations
 */
export function useKycApprovalForm(requestId, therapistId, mode = 'approve') {
  const [formData, setFormData] = useState(getEmptyFormData())
  const [therapist, setTherapist] = useState(null)
  const [activeTab, setActiveTab] = useState(0)
  const [isDirty, setIsDirty] = useState(false)
  const [saving, setSaving] = useState(false)

  // Section-level loading and errors
  const [loading, setLoading] = useState({
    request: false,
    kyc: false,
    functions: false,
    assignments: false,
    permissions: false,
    payout: false,
    ops: false,
  })

  const [errors, setErrors] = useState({
    kyc: null,
    functions: null,
    assignments: null,
    permissions: null,
    payout: null,
    ops: null,
  })

  /**
   * Load existing approval request
   */
  const loadRequest = async (reqId) => {
    if (!reqId) return

    setLoading((prev) => ({ ...prev, request: true }))
    try {
      const resp = await kycApprovalAPI.getRequest(reqId)

      if (resp.status === 200 && resp.data) {
        const data = resp.data

        // Set therapist details
        setTherapist(data.therapist)

        // Map section data from response
        const mappedFormData = {
          kyc: data.kyc?.data || getEmptyFormData().kyc,
          functions: data.functions?.data || getEmptyFormData().functions,
          assignments: data.assignments?.data || getEmptyFormData().assignments,
          permissions: data.permissions?.data || getEmptyFormData().permissions,
          payout: data.payout?.data || getEmptyFormData().payout,
          ops: data.ops?.data || getEmptyFormData().ops,
        }

        setFormData(mappedFormData)
      } else {
        toast.error('Failed to load approval request')
      }
    } catch (error) {
      console.error('Error loading request:', error)
      toast.error('Error loading approval request')
    } finally {
      setLoading((prev) => ({ ...prev, request: false }))
    }
  }

  /**
   * Load therapist details for new request
   */
  const loadTherapist = async (therapistSlug) => {
    if (!therapistSlug) return

    setLoading((prev) => ({ ...prev, request: true }))
    try {
      const resp = await getSPDataWithParams('/api/secure/cc/support/therapists/by-slug', {
        slug: therapistSlug,
      })

      if (resp.status === 200 && resp.data) {
        setTherapist({
          name: resp.data.name,
          slug: resp.data.slug,
          phone: resp.data.phone,
          profilePicId: resp.data.profilePicId,
        })
      } else {
        toast.error('Failed to load therapist details')
      }
    } catch (error) {
      console.error('Error loading therapist:', error)
      toast.error('Error loading therapist')
    } finally {
      setLoading((prev) => ({ ...prev, request: false }))
    }
  }

  /**
   * Update a specific section's data
   */
  const updateSection = (sectionKey, data) => {
    setFormData((prev) => ({
      ...prev,
      [sectionKey]: data,
    }))
    setIsDirty(true)
    // Clear error for this section
    setErrors((prev) => ({
      ...prev,
      [sectionKey]: null,
    }))
  }

  /**
   * Save and approve all visible sections
   */
  const saveAndApprove = async (visibleSections = ALL_SECTIONS) => {
    // Validate all sections
    const validation = validateAllSections(formData, visibleSections)
    if (!validation.valid) {
      toast.error(validation.error)
      setErrors((prev) => ({
        ...prev,
        [validation.section]: validation.error,
      }))
      return false
    }

    setSaving(true)

    try {
      let finalRequestId = requestId

      // If creating new request, create it first
      if (!requestId && therapistId) {
        const appRef = `APP-${new Date().getFullYear()}-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`
        const createResp = await kycApprovalAPI.createRequest(therapistId, appRef)

        if (createResp.status === 201 && createResp.data) {
          finalRequestId = createResp.data.id
        } else {
          throw new Error('Failed to create approval request')
        }
      }

      // Save and approve all sections
      await kycApprovalAPI.saveAndApproveAll(finalRequestId, formData, visibleSections)

      toast.success('Approval request saved and approved successfully!')
      setIsDirty(false)
      return true
    } catch (error) {
      console.error('Save and approve failed:', error)
      toast.error(error.message || 'Failed to save and approve')
      return false
    } finally {
      setSaving(false)
    }
  }

  /**
   * Reset form to empty state
   */
  const reset = () => {
    setFormData(getEmptyFormData())
    setIsDirty(false)
    setErrors({
      kyc: null,
      functions: null,
      assignments: null,
      permissions: null,
      payout: null,
      ops: null,
    })
  }

  // Load data on mount
  useEffect(() => {
    if (requestId) {
      loadRequest(requestId)
    } else if (therapistId) {
      loadTherapist(therapistId)
    }
  }, [requestId, therapistId])

  return {
    // State
    formData,
    therapist,
    activeTab,
    isDirty,
    loading,
    errors,
    saving,

    // Actions
    setActiveTab,
    updateSection,
    saveAndApprove,
    reset,
  }
}
